import '@ant-design/v5-patch-for-react-19';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React, { useCallback, useEffect } from 'react';
// import CustomSettingPanel from '@/components/CustomSettingPanel';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
// import { PermissionSwitch } from '@/components/RightContent';
import { permissionService } from '@/services/permissionCore';
import { getOpenV1ConfigIcon, getOpenV1ConfigItems } from '@/services/swagger/kaifangjiekou';
import {
  getPlatV1AccessMenus,
  getPlatV1AccessSelf,
} from '@/services/swagger/pingtaijiekou';
// import { safeTransformMenus, safeGetNavigationMenus } from '@/utils/safeMenuUtils';
import { forceFixMenuIcons } from '@/utils/forcedIconFix';
import { transformMenus } from '@/utils/menuUtils';
import { permissionUtils } from '@/utils/permissionUtils';

// import { filterMenus } from '@/utils/menuFilterUtils';
// import { useMenuFilter } from '@/hooks/useDynamicMenu';
// import { useEnhancedMenuTransform, useMenuIntl } from '@/hooks/useMenuIntl'; // 引入增强菜单转换 Hook

import { shouldUseBackendPermission } from '@/config/permissionConfig';
import { updateGlobalPermissions } from '@/middleware/apiPermissionGuard';
import {
  getLocalRouteMenus,
  getLocalRoutePermissions,
} from '@/utils/localRouteUtils';
import defaultSettingsConfig from '../config/defaultSettings';
import access from './access';
import { errorConfig } from './requestErrorConfig';
import LogoIcon from '@/assets/svgs/logo.svg';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.AccessSelfRes;
  menus?: API.AccessMenusItem[];
  permissions?: string[]; // 用户权限码列表
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.AccessSelfRes | undefined>;
  fetchMenus?: () => Promise<API.AccessMenusItem[] | undefined>;
  access?: ReturnType<typeof access>;
  menusLoaded?: boolean; // 标识菜单是否已加载
  configItems?: Record<string, any>; // 系统配置项
  fetchConfigItems?: () => Promise<Record<string, any> | undefined>; // 获取配置项方法
}> {
  // 动态设置 favicon 的函数
  const setFavicon = (base64Icon: string) => {
    console.log('设置 favicon:', base64Icon);
    try {
      // 查找现有的 favicon 链接
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      
      // 如果没有找到，创建一个新的
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      
      // 设置 href 为 base64 编码的图标
      link.href = base64Icon || LogoIcon;
      console.log('Favicon 已动态设置');
    } catch (error) {
      console.error('设置 favicon 失败:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const userInfo = await getPlatV1AccessSelf({});
      return userInfo as API.AccessSelfRes;
      // return {};
    } catch (_error) {
      // 避免在已经处理401错误的情况下重复跳转
      const { pathname } = history.location;
      if (pathname !== loginPath) {
        // 用户信息获取失败，跳转到登录页面
        history.push(loginPath);
      }
    }
    return undefined;
  };

  // 获取菜单数据
  const fetchMenus = async () => {
    try {
      // 直接调用 API 获取菜单数据，绕过复杂的权限服务
      const menuResponse = await getPlatV1AccessMenus({});

      // 处理后端返回的数据结构：{ code: 0, message: 'OK', data: { menus: [...] } }
      const menus =
        (menuResponse as any)?.data?.menus || menuResponse.menus || [];

      return menus;
    } catch (_error) {
      // 获取菜单失败
      // 不要在这里跳转登录，让 requestErrorConfig 统一处理
      return [];
    }
  };

  // 获取系统配置项（同时获取图标数据）
  const fetchConfigItems = async () => {
    try {
      // 并行请求两个接口
      const [configResponse, iconResponse] = await Promise.all([
        getOpenV1ConfigItems({}),
        getOpenV1ConfigIcon({}),
      ]);
      
      // 合并数据
      const configData = configResponse.data || {};
      
      // 将图标数据添加到配置项中，并添加 base64 前缀
      if (iconResponse?.image) {
        // 判断是否已经有 base64 前缀，如果没有则添加
        let iconValue = iconResponse.image;
        if (!iconValue.startsWith('data:')) {
          // 根据内容判断图片类型，默认使用 png
          // 如果包含 SVG 特征，使用 svg+xml，否则使用 png
          const isSvg = iconValue.includes('<svg') || iconValue.includes('<?xml');
          const mimeType = isSvg ? 'image/svg+xml' : 'image/png';
          iconValue = `data:${mimeType};base64,${iconValue}`;
        }
        configData.sys_icon = iconValue;
      }
      
      return configData;
    } catch (_error) {
      // 获取配置项失败，返回空对象
      return {};
    }
  };

  // 提取用户权限码 - 根据全局权限开关决定权限来源
  const extractUserPermissions = (menus: API.AccessMenusItem[]): string[] => {
    // 检查是否应该使用后端权限
    // 已移除：使用本地路由模式

    // 权限提取开始

    if (!shouldUseBackendPermission()) {
      // 使用本地路由权限 - 给予所有配置的路由权限
      const localPermissions = getLocalRoutePermissions();
      // 使用本地路由权限
      return localPermissions;
    }

    // 使用后端权限逻辑（原有逻辑）
    const permissions: string[] = [];

    const traverse = (menuItems: API.AccessMenusItem[]) => {
      menuItems.forEach((menu) => {
        // 给予所有菜单权限，确保菜单能够显示
        if (menu.menuId) {
          permissions.push(menu.menuId);
        }
        if (menu.menuComp) {
          permissions.push(menu.menuComp);
        }
        if (menu.menuPath) {
          permissions.push(menu.menuPath);
        }

        // 菜单项处理

        // 递归处理子菜单
        if (menu.children && menu.children.length > 0) {
          traverse(menu.children);
        }
      });
    };

    traverse(menus);

    // 去重并返回
    const uniquePermissions = [...new Set(permissions)];
    // 权限提取完成

    return uniquePermissions;
  };
  const { location } = history;

  // 获取系统配置项（在任何页面都需要获取）
  const configItems = await fetchConfigItems();
  
  // 尝试从配置项中获取 favicon
  // 优先级：sys_icon（图标接口） > sys_favicon（配置项） > favicon（配置项）
  const faviconConfig = 
    (configItems?.sys_icon && configItems.sys_icon.trim()) || 
    (configItems?.sys_favicon && configItems.sys_favicon.trim()) || 
    (configItems?.favicon && configItems.favicon.trim());
    console.log('faviconConfig:', faviconConfig);
  if (faviconConfig) {
    // 如果配置项中包含 base64 编码的图标，则设置 favicon
    if (typeof faviconConfig === 'object' && faviconConfig !== null && 'conf_val' in faviconConfig) {
      console.log('faviconConfig.conf_val:', faviconConfig.conf_val);
      setFavicon(faviconConfig.conf_val);
    } else if (typeof faviconConfig === 'string') {
      console.log('faviconConfig2:', faviconConfig);
      setFavicon(faviconConfig);
    }
  }else {
    setFavicon('')
  }

  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const menus = await fetchMenus();
    const permissions = extractUserPermissions(menus);
    const accessInstance = access({ currentUser, menus, permissions });

    // 初始化菜单权限工具（使用启用真实权限的服务实例）
    permissionUtils.initUserMenus(menus);

    // 菜单权限工具初始化完成

    // 更新全局权限数据
    updateGlobalPermissions(permissions);

    return {
      fetchUserInfo,
      fetchMenus,
      fetchConfigItems,
      currentUser,
      menus,
      permissions,
      configItems,
      access: accessInstance,
      settings: defaultSettingsConfig as Partial<LayoutSettings>,
      menusLoaded: true, // 标记菜单已加载
    };
  }
  // 登录页面不需要加载菜单数据，菜单数据应该在用户成功登录后再获取
  // 避免在用户未登录状态下尝试获取受保护的菜单资源

  // 登录页面状态处理
  return {
    fetchUserInfo,
    fetchMenus,
    fetchConfigItems,
    configItems,
    access: access({ currentUser: undefined, menus: [], permissions: [] }),
    settings: defaultSettingsConfig as Partial<LayoutSettings>,
    menusLoaded: false, // 标记菜单未加载
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 获取用户权限列表 - 优先使用initialState中的权限数据
  // const userPermissions = initialState?.permissions || [];

  // 提前调用 Hook，避免在条件语句中调用
  // const { translateMenuTree } = useMenuIntl();

  // 动态生成菜单数据 - 根据权限配置决定使用本地路由或后端数据
  const menuData = React.useMemo(() => {
    // 获取最新的权限配置
    const useBackendPermission = shouldUseBackendPermission();

    // 菜单数据生成开始

    if (!useBackendPermission) {
      // 使用本地路由配置生成菜单
      const localMenus = getLocalRouteMenus();

      // 应用国际化翻译
      const translatedLocalMenus = localMenus;

      const fixedLocalMenus = forceFixMenuIcons(translatedLocalMenus);

      return fixedLocalMenus;
    }

    // 使用后端权限数据生成菜单

    // 使用initialState中的真实菜单数据
    const apiMenus = initialState?.menus || [];
    const _userPermissions = initialState?.permissions || [];

    // 后端权限模式详细信息

    // 特别检查：如果 API 返回空数据，根据权限配置决定处理方式
    if (apiMenus.length === 0) {
      // 检查是否启用了后端权限模式
      if (shouldUseBackendPermission()) {
        // 在严格模式下，返回空菜单
        return [];
      } else {
        // 本地路由模式下，回退到本地配置
        const localMenus = getLocalRouteMenus();
        const translatedLocalMenus = localMenus;
        const fixedLocalMenus = forceFixMenuIcons(translatedLocalMenus);

        return fixedLocalMenus;
      }
    }

    // 检查菜单数据的完整性
    if (apiMenus.length === 1) {
      // 菜单数据异常：只获取到1个菜单项，可能是API数据不完整
    }

    // 后端菜单数据处理

    // 初始化菜单权限工具
    if (apiMenus.length > 0) {
      // 初始化菜单权限工具
      permissionUtils.initUserMenus(apiMenus);

      // 验证初始化结果
      // const menuMapSize = (menuPermissionUtils as any).menuMap?.size || 0;
      // 菜单映射表信息
    }

    // 过滤出用户有权限访问的菜单
    const accessibleMenus = permissionUtils.getAccessibleMenuTree();

    // 菜单权限过滤完成

    // 转换为 ProLayout 格式
    const transformedMenus = transformMenus(accessibleMenus, {
      includeHidden: false,
      forNavigation: true,
    });

    // 检查原始菜单数据

    // 后端权限模式下，直接使用后端返回的菜单标题和图标，不进行本地转换
    // 这样菜单栏的文字和图标完全按照 menus 接口返回的数据执行
    const backendMenus = transformedMenus.map((menu) => {
      // 递归处理子菜单
      const processMenu = (m: any, level: number = 0): any => {
        const rawMenu = m.__rawMenu;

        const result: any = {
          ...m,
          // ✅ 关键修复：直接使用后端返回的 menuTitle 作为菜单名称
          name: rawMenu?.menuTitle || m.name,
          // ✅ 关键修复：确保图标不被覆盖（一级菜单和子菜单都要保留图标）
          icon: m.icon,
        };

        // 处理子菜单 (ProLayout 使用 routes 作为子菜单)
        if (m.routes && m.routes.length > 0) {
          result.routes = m.routes.map((child: any) =>
            processMenu(child, level + 1),
          );
        }

        return result;
      };

      return processMenu(menu);
    });

    // 🔍 调试：检查最终生成的子菜单图标
    const checkChildMenuIcons = (menus: any[], level = 0) => {
      let childCount = 0;
      let childWithIcons = 0;

      menus.forEach((menu) => {
        if (level > 0) {
          childCount++;
          if (menu.icon) childWithIcons++;
        }
        if (menu.routes && menu.routes.length > 0) {
          const childResult = checkChildMenuIcons(menu.routes, level + 1);
          childCount += childResult.childCount;
          childWithIcons += childResult.childWithIcons;
        }
      });

      return { childCount, childWithIcons };
    };

    const _childMenuStats = checkChildMenuIcons(backendMenus);

    // 🔍 关键修复：尝试强制 ProLayout 渲染子菜单图标
    // ProLayout 可能需要明确的 icon 属性才能显示子菜单图标
    const forceRenderIcons = (menus: any[]) => {
      return menus.map((menu) => {
        const result: any = {
          ...menu,
          // ✅ 确保 icon 属性存在且不为 undefined
          icon: menu.icon || undefined,
          // ✅ 添加 key 避免 React 重复渲染
          key: menu.path || menu.name,
        };

        if (menu.routes && menu.routes.length > 0) {
          result.routes = forceRenderIcons(menu.routes);
        }

        return result;
      });
    };

    const finalMenus = forceRenderIcons(backendMenus);

    // 后端菜单生成成功（使用后端返回的原始数据）

    return finalMenus;
  }, [initialState?.menus, initialState?.permissions]); // 添加依赖以响应数据变化

  // 监听设置抽屉打开状态
  useEffect(() => {
    if (!isDev) return;

    // 可以在这里添加其他开发工具的监听逻辑
  }, []);

  // 刷新菜单数据的函数
  const refreshMenus = useCallback(async () => {
    try {
      // 开始刷新菜单数据

      // 强制刷新权限数据
      const permissionData = await permissionService.refresh();
      const newMenus = permissionData.menus;
      const newPermissions = permissionData.userPermissions;

      // 刷新菜单数据完成

      if (setInitialState) {
        setInitialState((s: any) => ({
          ...s,
          menus: newMenus,
          permissions: newPermissions,
          menusLoaded: true,
        }));
      }

      // 重新初始化菜单权限工具
      permissionUtils.initUserMenus(newMenus);

      return { menus: newMenus, permissions: newPermissions };
    } catch (error) {
      console.error('刷新菜单失败:', error);
      return { menus: [], permissions: [] };
    }
  }, [setInitialState]);

  // 定期自动刷新菜单数据（每5分钟）
  useEffect(() => {
    const intervalId = setInterval(
      () => {
        refreshMenus().catch((error: any) => {
          console.warn('自动刷新菜单失败:', error);
        });
      },
      5 * 60 * 1000,
    ); // 5分钟

    return () => clearInterval(intervalId);
  }, [refreshMenus]);

  // 如果菜单为空且未加载过，尝试重新获取
  React.useEffect(() => {
    if (
      (!initialState?.menus || initialState.menus.length === 0) &&
      !initialState?.menusLoaded
    ) {
      refreshMenus();
    }
  }, [initialState]);

  // 监控菜单数据变化
  useEffect(() => {
    if (initialState?.menus && initialState.menus.length > 0) {
      // 重新初始化权限工具
      permissionUtils.initUserMenus(initialState.menus);
    }
  }, [initialState?.menus]);

  return {
    menu: {
      // 明确指定使用我们生成的动态菜单数据
      request: async () => {
        // ProLayout menu.request 被调用
        return menuData;
      },
      // 禁用默认的本地菜单数据
      locale: false,
      // 精确匹配路径，避免模糊匹配导致的选中冲突
      matchMenuKeys: (pathname: string, menuData: any[]) => {
        // 菜单路径匹配检查
        // 菜单路径匹配完成

        // 更严格的精确匹配逻辑
        const findExactMatch = (items: any[]): any => {
          for (const item of items) {
            // 精确路径匹配 - 完全相等
            if (item.path === pathname) {
              // 精确路径匹配
              return item;
            }

            // 处理重定向情况 - 如果当前路径是某个菜单的重定向目标
            if (item.redirect && item.redirect === pathname) {
              // 重定向匹配
              return item;
            }

            // 检查子路由
            if (item.routes && item.routes.length > 0) {
              const childMatch = findExactMatch(item.routes);
              if (childMatch) {
                // 子路由精确匹配
                // 返回父级菜单路径以便正确高亮
                return item;
              }
            }
          }
          return null;
        };

        // 特殊处理系统管理子菜单的精确匹配
        const handleSystemSubMenu = (items: any[]): string[] => {
          // 系统管理相关的特殊路径处理
          const systemPaths = [
            '/system/menu',
            '/system/role',
            '/system/dict',
            '/system/sys-config',
            '/system/no-auth',
          ];

          if (systemPaths.includes(pathname)) {
            // 系统管理子菜单特殊处理

            // 查找对应的系统管理子菜单
            for (const item of items) {
              if (item.routes && item.routes.length > 0) {
                // 遍历系统管理的子路由
                for (const subItem of item.routes) {
                  if (subItem.path === pathname) {
                    // 找到精确匹配的系统子菜单
                    // 只返回当前子菜单的路径，不返回父级路径
                    return [subItem.path];
                  }
                }
              }
            }
          }

          return [];
        };

        // 首先处理系统管理子菜单的特殊情况
        const systemSubMenuMatch = handleSystemSubMenu(menuData);
        if (systemSubMenuMatch.length > 0) {
          // 系统子菜单匹配成功
          return systemSubMenuMatch;
        }

        // 然后尝试常规精确匹配
        const exactMatch = findExactMatch(menuData);

        if (exactMatch) {
          // 最终匹配结果
          return [exactMatch.path];
        }

        // 如果没有精确匹配，尝试前缀匹配（但要避免冲突）
        const prefixMatches = menuData.filter((item) => {
          // 检查是否为前缀匹配且不会造成冲突
          if (pathname.startsWith(item.path) && item.path !== '/') {
            // 避免 /system 匹配到 /system/role 这种情况
            const remainingPath = pathname.substring(item.path.length);
            // 只有当剩余路径为空或以/开头时才认为是匹配
            // 但对于系统管理这类有明确子路由的情况，要更加严格
            if (item.path === '/system') {
              // 系统管理父级菜单只在访问 /system 根路径时才匹配
              return remainingPath === '' || remainingPath === '/';
            }
            return remainingPath === '' || remainingPath.startsWith('/');
          }
          return false;
        });

        if (prefixMatches.length === 1) {
          // 前缀匹配成功
          return [prefixMatches[0].path];
        } else if (prefixMatches.length > 1) {
          // 如果有多个前缀匹配，选择最精确的那个
          const bestMatch = prefixMatches.reduce((prev, current) => {
            return current.path.length > prev.path.length ? current : prev;
          });
          // 多重前缀匹配，选择最优
          return [bestMatch.path];
        }

        // 如果还是没有匹配，返回空数组避免模糊匹配
        // 未找到任何匹配
        return [];
      },
    },
    // 禁用默认的菜单过滤
    defaultOpenAll: false,
    title: (() => {
      const sysNameItem = initialState?.configItems?.sys_name;
      if (
        typeof sysNameItem === 'object' &&
        sysNameItem !== null &&
        'conf_val' in sysNameItem
      ) {
        return sysNameItem.conf_val || '智能视频融合平台';
      }
      return sysNameItem || '智能视频融合平台';
    })(),
    // 添加 logo 配置
    logo: (() => {
      // 优先使用接口返回的图标
      const iconConfig = initialState?.configItems?.sys_icon;
      if (iconConfig && typeof iconConfig === 'string' && iconConfig.trim() !== '') {
        return iconConfig;
      }
      // 回退到默认 logo
      return LogoIcon;
    })(),
    actionsRender: () => [
      // 已隐藏：帮助文档图标
      // <Question key="doc" />,
      // 已隐藏：多语言切换
      // <SelectLang key="SelectLang" />,
      // 已隐藏：权限开关
      // <PermissionSwitch key="PermissionSwitch" />,
    ],
    avatarProps: {
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.nickname,
    },
    footerRender: () => <Footer />,
    childrenRender: (children) => (
      <>
        {children}
        {isDev && (
          <SettingDrawer
            disableUrlParams
            enableDarkTheme
            settings={initialState?.settings}
            onSettingChange={(settings) => {
              setInitialState((preInitialState) => ({
                ...preInitialState,
                settings,
              }));
            }}
          />
        )}
      </>
    ),
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        // 避免重复跳转，让requestErrorConfig统一处理401错误
        // 页面变更检测到未登录状态
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    // 已隐藏：OpenAPI文档链接（仅在开发环境显示）
    // links: isDev
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPI文档</span>
    //       </Link>,
    //     ]
    //   : [],
    menuHeaderRender: undefined,
    // 设置内容区域的滚动行为
    contentStyle: {
      overflow: 'auto',
      height: 'calc(100vh - 56px)',
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '/',
  ...errorConfig,
};
